





























































import {Component, Inject, Prop, Vue, Watch} from 'vue-property-decorator';
import {
	ERouteName
} from '../../router';
import {
	CAMPAIGN_ASSET_SAVE_TYPE,
	CampaignStore,
	CustomerProfileStore,
	EMBED_MODE,
	ElementConfigStore,
	SessionStore,
	UIStore,
	isValidAssetSaveType,
} from '../../store';
import {
	WebService,
} from '../../services';
import {TermsOfService} from '../forms';

import type {RawLocation, Route} from 'vue-router';
import type {VueComponent} from '../../types';
import type {
	ICampaignTypeLandingPage,
	ILBConfiguredElement,
	ILandingPageElement,
	ILandingPageElementOffer,
	TAssetSaveType,
	TLandingPageLayoutKey,
} from '../../store';

// import Element components
import {LBElements} from './layout-builder-elements';


const ALLOWED_DOMAINS_RE: RegExp[] = [
	/\.tp-devel\.com(:\d+)?$/,
	/\.tp-qa\.com(:\d+)?$/,
	/\.tchpt\.com(:\d+)?$/,
];

@Component({
	components: {
		...LBElements,
		TermsOfService,
	},
})
export default class Landing extends Vue {
	@Inject('BalloonImageContainer') readonly injectedBalloonImageContainer!: VueComponent;
	@Inject('Button') readonly injectedButton!: VueComponent;
	@Inject('IconGratuityLevel2') readonly injectedIconGratuityLevel2!: VueComponent;
	@Inject('Logo') readonly injectedLogo!: VueComponent;

	@Prop({default: null})
	campaignId!: ICampaignTypeLandingPage['id'] | null;

	/**
	 * list of landing page elements to display
	 *
	 * While editing (isMPEdit), these come from Web Admin's MessageChannel.
	 * Otherwise, they are loaded with the campaign.
	 */
	previewElements: ILandingPageElement[] = [];

	initiallyLoggedIn = false;
	isLoading = true;
	previewPort: MessagePort | null = null;
	showTerms = false;
	showWelcome = false;

	get assetSaveType(): TAssetSaveType {
		const assetSaveType = this.$route.query?.assetSaveType;

		if(typeof assetSaveType !== 'undefined' &&
			isValidAssetSaveType(assetSaveType as string)
		) {
			return assetSaveType as TAssetSaveType;
		}

		if(this.isMPEdit) {
			return CAMPAIGN_ASSET_SAVE_TYPE.DRAFT;
		}

		return CAMPAIGN_ASSET_SAVE_TYPE.PUBLISHED;
	}

	get balloonImage(): VueComponent {
		return this.injectedIconGratuityLevel2;
	}

	get buttonLabel(): string {
		if(this.selectedLayout === 'has_account') {
			return 'Continue to Home';
		}

		return 'Continue';
	}

	get elements(): ILandingPageElement[] {
		if(this.isMPEdit) {
			return this.previewElements;
		}

		// CPH - TODO: How does someone get to a layout that actually has something if selectedLayout does not?
		return this.campaign?.layouts.find(l => l.key === this.selectedLayout)?.elements ?? [];
	}

	get campaign(): ICampaignTypeLandingPage | null {
		if(this.campaignId === null) {
			return null;
		}

		return CampaignStore.campaign(
			this.campaignId,
			this.assetSaveType
		) as ICampaignTypeLandingPage;
	}

	get campaignExists(): boolean {
		return (this.campaign !== null &&
			(
				// consider it as not existing if no elements and not embedded
				(this.layoutHasElements(this.selectedLayout) && this.isNotEmbedded) ||
				(!this.isNotEmbedded)
			)
		);
	}

	get configuredElements(): ILBConfiguredElement[] {
		const configKeys = Object.keys(ElementConfigStore?.config);

		return this.elements
			.map(element => {
				if(typeof element?.type === 'string' &&
					configKeys.includes(element.type)
				) {
					const {component, ...data} = ElementConfigStore.config[element.type];

					return {
						component,
						data,
						element,
					};
				}

				return null;
			})
			.filter(el => el !== null) as ILBConfiguredElement[]
		;
	}

	get customerProfile() {
		return CustomerProfileStore.customerProfile;
	}

	get isEmpty(): boolean {
		return this.elements.length === 0;
	}

	/**
	 * indicates that page is loaded in Marketing Portal iframe for interactivity
	 *
	 * mutually exclusive to isMPPreview
	 *
	 * "Edit Mode" may add additional styling (e.g., borders) or instructions to
	 * the user.
	 */
	get isMPEdit(): boolean {
		return UIStore.embedMode === EMBED_MODE.EDIT;
	}

	/**
	 * indicates that page is loaded in Marketing Portal iframe for display only
	 *
	 * mutually exclusive to isMPEdit
	 */
	get isMPPreview(): boolean {
		return UIStore.embedMode === EMBED_MODE.PREVIEW;
	}

	get isNotEmbedded(): boolean {
		return UIStore.embedMode === EMBED_MODE.NONE;
	}

	get selectedLayout(): TLandingPageLayoutKey {
		// Default to the logged out layout
		let selectedLayout: TLandingPageLayoutKey = 'landing';

		if(this.$route.query?.layout) {
			selectedLayout = this.$route.query.layout as TLandingPageLayoutKey;
		} else if(this.isLoggedIn) {
			// If we are not provided a layout in the query params, we need to use logged
			// in status to determine which layout to display
			// CPH - TODO: When a user comes back, how do we tell if they signed up via this Landing Page. Do we store something on the Customer Opportunity?
			// CPH - TODO: What if the campaign doesn't have all the layouts configured
			selectedLayout = 'has_account';
		}

		return selectedLayout;
	}

	get isLoggedIn() {
		return SessionStore.authConfig !== null;
	}

	/**
	 * shorthand for checking existence of second layout
	 *
	 * Yes, we could just use `layoutHasElements` directly, but this
	 * decouples what constitutes a single landing page from the
	 * only currently-defined second layout (i.e., 'has_account').
	 */
	get landingIsSinglePage(): boolean {
		return !this.layoutHasElements('has_account');
	}

	get showContinue(): boolean {
		return (this.isLoggedIn && !this.showTerms && !this.showWelcome) || // after 'landing', but not during TOS or balloons
			this.landingIsSinglePage || // if only one page
			!this.campaignExists // if no campaign
		;
	}

	get showDraftWarning() {
		return !this.isMPEdit &&
			this.assetSaveType === CAMPAIGN_ASSET_SAVE_TYPE.DRAFT
		;
	}

	/**
	 * watch customerProfile for changes
	 *
	 * We don't fetch customerProfile directly from this component. That
	 * is done from WebService.startLoggedIn, via App's authConfig watch.
	 * Here, instead, watch the store value for changes and treat that as
	 * a promise indicating it is OK to proceed (as done in App).
	 */
	@Watch('customerProfile')
	async onChangeCustomerProfile() {
		if(this.customerProfile && this.isLoggedIn) {
			await WebService.loggedInStarted;
			if(!this.customerProfile?.acceptedTOS) {
				this.showTerms = true;
			} else if(!this.initiallyLoggedIn && !this.showTerms && !this.showWelcome) {
				this.showLayout('has_account').then();

				/*
				 * We'll likely get bumped around by App's authConfig watch
				 * calling WebService.handleScreenPops. In case that happens,
				 * reset the initial path to the 'has_account' layout.
				 */
				SessionStore.setInitialPath(WebService.getCurrentPath());
			}
		}
	}

	async created() {
		/*
		 * We may need to redirect to other components, namely Login or AcceptTOS.
		 * This requires stashing the embed mode in a persistent location. In the
		 * conditionals below, give edit precedence over preview.
		 */
		if(typeof this.$route.query?.mpEdit !== 'undefined') {
			UIStore.setEmbedMode(EMBED_MODE.EDIT);
		} else if(typeof this.$route.query?.mpPreview !== 'undefined') {
			UIStore.setEmbedMode(EMBED_MODE.PREVIEW);
		} else {
			UIStore.setEmbedMode(EMBED_MODE.NONE);
		}

		/*
		 * If the user isn't logged in and trying to visit 'has_account',
		 * redirect back to 'landing'.
		 */
		if(UIStore.embedMode === EMBED_MODE.NONE &&
			this.selectedLayout === 'has_account' &&
			!this.isLoggedIn
		) {
			await this.showLayout('landing');
		}

		SessionStore.setInitialPath(WebService.getCurrentPath());

		/*
		 * generate landing page according to the selected layout
		 *
		 * N.B.: The `layout` query param should correspond to one of the
		 *       keys in Marketing Portal's LAYOUT_PREVIEW_DEFINITIONS.
		 */
		try {
			if(this.isMPEdit) {
				window.addEventListener('message', this.receiveOpenChannel);
			}
			await WebService.start();
			// noinspection ES6MissingAwait
			const promises = [
				SessionStore.fetchBrandConfig(),
				ElementConfigStore.fetchConfig()
			];
			if(this.campaignId) {
				let check = this.$route.query?.check;
				if(Array.isArray(check)) {
					check = check?.[0] ?? '';
				}

				promises.push(CampaignStore.fetchCampaign({
					campaignId: this.campaignId,
					check,
					/*
					 * In cases where assetSaveType isn't specified (e.g., when viewed directly
					 * by a customer), default to published.
					 */
					includeSaveTypes: [this.assetSaveType ?? CAMPAIGN_ASSET_SAVE_TYPE.PUBLISHED],
				}));
			}
			await Promise.all(promises);

			if(this.customerProfile && this.isLoggedIn) {
				this.initiallyLoggedIn = true;
			}
		} catch(error) {
			console.warn(error);
		} finally {
			this.isLoading = false;
		}

		// We need data returned from this before we display the landing page to the user
		await this.logLandingPageView();
	}

	beforeUnmount() {
		console.debug('removeEventListener message');
		window.removeEventListener('message', this.receiveOpenChannel);
	}


	acceptTOS() {
		this.showWelcome = true;

		// allow Welcome to render before hiding TOS
		this.$nextTick(() => {
			this.showTerms = false;

			setTimeout(() => {
				this.showLayout('has_account').catch(() => {
					// ignore duplicate route errors
				}).finally(async () => {
					await this.logLandingPageView();
					this.showWelcome = false;
				});
			}, 5000);
		});
	}

	emitError(error: Error | string) {
		try {
			(this.previewPort as MessagePort).postMessage({error});
		} catch(e) {
			console.error('Unable to signal error.', e);
		} finally {
			console.error(error);
		}
	}

	async logLandingPageView() {
		if(this.isNotEmbedded &&
			this.isLoggedIn &&
			this.campaignId &&
			this.assetSaveType === CAMPAIGN_ASSET_SAVE_TYPE.PUBLISHED
		) {
			try {
				// disregard selectedLayout--offers are only available from 'has_account'
				const layoutElements = this.campaign?.layouts.find(l => l.key === 'has_account')?.elements ?? [];
				const offerIds = layoutElements.filter(element => element.type === 'offer').map(element => (element as ILandingPageElementOffer).value.offerId) ?? [];

				await CampaignStore.landingPageView({
					campaignId: this.campaignId,
					offerIds: offerIds
				});
			} catch(error) {
				console.warn('Failed to log view for landing page', error);
			}
		}
	}

	async goToNext() {
		this.isLoading = true;

		let switchCase: TLandingPageLayoutKey | null = this.selectedLayout;

		// if only one page or no campaign, continue to Login
		if(!this.campaignExists || this.landingIsSinglePage) {
			switchCase = null;
		}

		switch(switchCase) {
			case 'landing':
				await this.showLayout('has_account');
				break;
			case 'has_account':
				SessionStore.setInitialPath(null);
				await this.$router.push({name: ERouteName.Home});
				break;
			default:
				SessionStore.setInitialPath(null);
				// TODO: We need to centralize the logic of how to get to "app start".
				//       We *can* route only to Login, but then the login form shows briefly before redirecting.
				if(this.isLoggedIn) {
					await this.$router.push({name: ERouteName.Home});
				} else {
					await this.$router.push({name: ERouteName.Login});
				}
				break;
		}
		this.isLoading = false;
	}

	handleError(event: MessageEvent) {
		this.emitError(`Message error. Data: ${JSON.stringify(event.data)}`);
	}

	layoutHasElements(layoutKey: TLandingPageLayoutKey): boolean {
		return (this.campaign?.layouts.find(l => l.key === layoutKey)?.elements.length ?? 0) > 0;
	}

	/**
	 * handles TOS logout action
	 *
	 * If the user logs out via TermsOfService, hide the TOS to
	 * "return" to the 'landing' layout.
	 */
	logout() {
		this.showTerms = false;
	}

	receiveMessage(event: MessageEvent) {
		try {
			const elements = JSON.parse(event.data);
			Vue.set(this, 'previewElements', elements);

			// reply
			(this.previewPort as MessagePort).postMessage({
				result: `${elements.length} elements received`,
			});
		} catch(e) {
			this.emitError(e);
		}
	}

	receiveOpenChannel(event: MessageEvent) {
		/*
		 * Do we trust the sender of this message? Check that the hostname
		 * matches one of the RegExps in ALLOWED_DOMAINS_RE.
		 */
		const originUrl = new URL(event.origin);
		if(!ALLOWED_DOMAINS_RE.some((hostnameRE) => hostnameRE.test(originUrl?.hostname))) {
			// console.error('Unexpected origin', event.origin);
			return;
		}

		if(event.ports?.length > 0) {
			console.debug('initializing receive port');

			// we no longer need to listen on the window
			window.removeEventListener('message', this.receiveOpenChannel);

			// save the port for easy access
			this.previewPort = event.ports[0];

			// reply to the initializer and listen for further messages
			this.previewPort.postMessage({
				result: 'Landing Preview ready to receive',
			});
			this.previewPort.onmessage = this.receiveMessage;
			this.previewPort.onmessageerror = this.handleError;
		}
	}

	showLayout(layout: TLandingPageLayoutKey): Promise<Route> {
		return this.$router.push({
			name: this.$route.name,
			query: {
				...this.$route.query,
				layout,
			},
		} as RawLocation);
	}
}
